import { useEffect, useState, useCallback } from 'react';
import { CreditCard, TrendingUp, Clock, RefreshCw, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { paymentService } from '../../services/paymentService';
import { UI, buttonStyle, ui } from '../../utils/ui';

const today = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const eventBadge = (event) => {
  const map = {
    order_created:      { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' },
    payment_captured:   { bg: '#dcfce7', color: '#16a34a', border: '#86efac' },
    verification_failed:{ bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
    refund_initiated:   { bg: '#dbeafe', color: '#2563eb', border: '#93c5fd' },
  };
  const c = map[event] || map.order_created;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '3px 9px',
      borderRadius: 9999, fontSize: 11, fontWeight: 900,
      backgroundColor: c.bg, color: c.color, border: `1px solid ${c.border}`,
      whiteSpace: 'nowrap',
    }}>
      {event.replace(/_/g, ' ')}
    </span>
  );
};

const SummaryCard = ({ label, value, icon: Icon, color }) => (
  <div style={{ ...ui.card, padding: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
    <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={20} color={color} />
    </div>
    <div>
      <p style={{ margin: 0, fontSize: 12, color: UI.colors.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontFamily: UI.font.heading, fontSize: 22, fontWeight: 900, color: UI.colors.text }}>{value}</p>
    </div>
  </div>
);

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const PaymentReports = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [pendingFrom, setPendingFrom] = useState(firstOfMonth());
  const [pendingTo, setPendingTo] = useState(today());
  const [refunding, setRefunding] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentService.getPaymentReport(from, to);
      setReport(res.data);
    } catch (err) {
      toast.error('Failed to load payment report.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleApply = () => {
    setFrom(pendingFrom);
    setTo(pendingTo);
  };

  const doRefund = async (bookingId) => {
    setRefunding(bookingId);
    try {
      await paymentService.refundPayment(bookingId);
      toast.success('Refund initiated. It will reflect in 5–7 business days.');
      fetchReport();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Refund failed.');
    } finally {
      setRefunding(null);
    }
  };

  const handleRefund = (bookingId) => {
    toast((t) => (
      <div>
        <p style={{ margin: '0 0 10px', fontWeight: 900, color: UI.colors.text }}>Issue full refund for this booking?</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => { toast.dismiss(t.id); doRefund(bookingId); }}
            style={{ ...buttonStyle('danger'), padding: '8px 12px', borderRadius: 12 }}
          >
            Yes, Refund
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ ...buttonStyle('outline'), padding: '8px 12px', borderRadius: 12 }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const handleExportCSV = () => {
    if (!report?.logs?.length) return;
    const headers = ['Date', 'Booking Ref', 'Customer', 'Room', 'Amount', 'Event', 'Razorpay Payment ID', 'Razorpay Refund ID'];
    const rows = report.logs.map((l) => [
      new Date(l.created_at).toLocaleString('en-IN'),
      l.booking_reference || '',
      l.customer_name || '',
      l.room_name || '',
      l.amount || '',
      l.event,
      l.razorpay_payment_id || '',
      l.razorpay_refund_id || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-report-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const s = report?.summary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: UI.font.heading, fontSize: 30, fontWeight: 900, margin: 0, color: UI.colors.text }}>Payment Reports</h1>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: UI.colors.muted }}>Monitor all Razorpay transactions and issue refunds</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={!report?.logs?.length}
          style={{ ...buttonStyle('outline'), padding: '10px 16px', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 8, opacity: !report?.logs?.length ? 0.5 : 1 }}
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Date filter */}
      <div style={{ ...ui.card, padding: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: UI.colors.muted, marginBottom: 4 }}>From</label>
          <input
            type="date"
            value={pendingFrom}
            onChange={(e) => setPendingFrom(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 10, border: `1.5px solid ${UI.colors.border}`, fontSize: 14, fontWeight: 700, color: UI.colors.text, outline: 'none' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 800, color: UI.colors.muted, marginBottom: 4 }}>To</label>
          <input
            type="date"
            value={pendingTo}
            onChange={(e) => setPendingTo(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 10, border: `1.5px solid ${UI.colors.border}`, fontSize: 14, fontWeight: 700, color: UI.colors.text, outline: 'none' }}
          />
        </div>
        <button
          onClick={handleApply}
          style={{ ...buttonStyle('primary'), padding: '10px 20px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <RefreshCw size={15} /> Apply
        </button>
      </div>

      {/* Summary cards */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
          <div style={{ width: 36, height: 36, border: `3px solid ${UI.colors.border}`, borderTopColor: UI.colors.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <SummaryCard label="Total Collected" value={fmt(s?.total_collected)} icon={TrendingUp} color="#16a34a" />
            <SummaryCard label="Pending Payments" value={fmt(s?.total_pending)} icon={Clock} color="#d97706" />
            <SummaryCard label="Total Refunded" value={fmt(s?.total_refunded)} icon={RefreshCw} color="#2563eb" />
            <SummaryCard label="Net Revenue" value={fmt(s?.net_revenue)} icon={CreditCard} color="#16a34a" />
          </div>

          {/* Logs table */}
          <div style={{ ...ui.card, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${UI.colors.border}` }}>
              <h2 style={{ fontFamily: UI.font.heading, fontSize: 18, fontWeight: 900, margin: 0, color: UI.colors.text }}>
                Payment Logs
                <span style={{ marginLeft: 10, fontSize: 13, fontWeight: 700, color: UI.colors.muted }}>({report?.logs?.length || 0} entries)</span>
              </h2>
            </div>

            {!report?.logs?.length ? (
              <div style={{ textAlign: 'center', padding: '48px 18px', color: UI.colors.muted, fontSize: 15, fontWeight: 800 }}>
                No payment logs found for this date range.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb' }}>
                      {['Date & Time', 'Booking Ref', 'Customer', 'Room', 'Amount', 'Event', 'Razorpay Payment ID', 'Action'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 900, color: UI.colors.muted, borderBottom: `1px solid ${UI.colors.border}`, whiteSpace: 'nowrap' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.logs.map((log) => (
                      <tr key={log.id} style={{ borderBottom: `1px solid ${UI.colors.border}` }}>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap', color: UI.colors.muted, fontWeight: 700 }}>
                          {new Date(log.created_at).toLocaleString('en-IN')}
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 900, color: UI.colors.text, whiteSpace: 'nowrap' }}>
                          {log.booking_reference || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', color: UI.colors.text, fontWeight: 700 }}>
                          <div>{log.customer_name || '—'}</div>
                          {log.customer_email && <div style={{ fontSize: 11, color: UI.colors.muted }}>{log.customer_email}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', color: UI.colors.text, fontWeight: 700 }}>
                          {log.room_name || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 900, color: UI.colors.primary, whiteSpace: 'nowrap' }}>
                          {log.amount ? fmt(log.amount) : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {eventBadge(log.event)}
                        </td>
                        <td style={{ padding: '12px 16px', color: UI.colors.muted, fontWeight: 700, fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {log.razorpay_payment_id || log.razorpay_order_id || '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {log.payment_status === 'paid' && (
                            <button
                              onClick={() => handleRefund(log.booking_id)}
                              disabled={refunding === log.booking_id}
                              style={{
                                ...buttonStyle('danger'),
                                padding: '6px 12px',
                                borderRadius: 10,
                                fontSize: 12,
                                opacity: refunding === log.booking_id ? 0.75 : 1,
                                display: 'flex', alignItems: 'center', gap: 6,
                              }}
                            >
                              {refunding === log.booking_id ? (
                                <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                              ) : (
                                <RefreshCw size={12} />
                              )}
                              Refund
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentReports;
